import { useParams, Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import { siteInfo } from '@/mocks/site';
import SEO from '@/components/feature/SEO';
import { OrganizationSchema, WebsiteSchema, WebPageSchema, BreadcrumbListSchema } from '@/components/base/SchemaOrg';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);

  if (!category) {
    return (
      <>
        <SEO title="分类未找到" description="抱歉，您要查找的分类不存在。" noIndex />
        <Navbar />
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-3">分类未找到</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700 cursor-pointer">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const categoryArticles = articles.filter((a) => a.category === category.slug);

  return (
    <>
      <SEO
        title={`${category.name} - 高义白洁`}
        description={category.description}
        keywords={`高义白洁,${category.name},君子品格,高洁志节,传统美德`}
        canonicalPath={`/category/${category.slug}`}
        ogImage={category.image}
      />
      <OrganizationSchema />
      <WebsiteSchema />
      <WebPageSchema
        pageUrl={`/category/${category.slug}`}
        pageTitle={`${category.name} - 高义白洁`}
        pageDescription={category.description}
      />
      <BreadcrumbListSchema
        items={[
          { name: '首页', item: '/' },
          { name: category.name },
        ]}
      />

      <div className="min-h-screen">
        <Navbar />
        <main className="pt-16">
          <Breadcrumb items={[{ label: '首页', href: '/' }, { label: category.name }]} />

          <section className="w-full py-10 md:py-14 bg-background-50">
            <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
              <div className="text-center mb-10">
                <div className="w-16 h-16 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mx-auto mb-4">
                  <i className={`${category.icon} text-2xl`}></i>
                </div>
                <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-3">
                  {category.name}
                </h1>
                <p className="text-foreground-600 max-w-xl mx-auto leading-relaxed">
                  {category.description}
                </p>
              </div>
            </div>
          </section>

          <section className="w-full py-8 md:py-12 bg-background-100">
            <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
              <nav className="flex items-center gap-3 flex-wrap mb-8" aria-label="内容分类导航">
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.slug}`}
                    className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                      cat.slug === slug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-50 text-foreground-600 hover:text-foreground-900 border border-background-200/70 hover:border-primary-300'
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </nav>

              {categoryArticles.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {categoryArticles.map((article) => (
                    <ArticleCard key={article.id} {...article} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-16">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-background-200 text-foreground-400 mx-auto mb-4">
                    <i className="ri-file-list-3-line text-2xl"></i>
                  </div>
                  <p className="text-foreground-500">此分类暂无文章，敬请期待</p>
                </div>
              )}
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}